// Motion previews for feature and integration pages, keyed by route ("features/<key>",
// "integrations/<slug>"). FeatureDetailPage and pages/integrations/[slug].vue render an entry
// through components/motion/MotionStage.vue; a route without an entry renders nothing.
//
// Rules (deliverables/motion-pages-2026-10-19/BRIEF.md):
// - Draw a slice of a real app screen. Every label is the app's own string (lib/l10n/app_en.arb
//   on Workspace369 origin/main); the comment above each entry names the screen and keys. A string
//   with no arb key gets a source comment on its line (file, or repo and file), so it can be
//   re-checked against the product when that file changes.
// - Sample data only: invented clients, numbers and people.
// - 3-5 beats ending on the payoff. Times (`at`, `swap`, `press.at`) are fractions of the 5 s
//   cycle; finish every beat by about 0.72 so the last frame holds before the next play.
// - Copy is English and goes through the normal translation pipeline; nothing here is a claim the
//   page copy doesn't already make.
//
// Entry: { title, icon (data/motion-icons.js), tier (1 Cadet … 5 Enterprise), dark, height,
// heightSm, and either scene (a bespoke scene component) or blocks (components/motion/
// MotionScene.vue documents each block type). }
export const featureMotion = {
  // Google busy time taking times off a booking page (components/motion/GcalBusyScene.vue): a
  // team member's Google day above the time step of their public booking page. Server-rendered
  // (hero). The rule line is app_en.arb settingsIntegrationsCalendarConflictsHelper.
  "integrations/google-calendar": {
    scene: "gcal-busy",
    title: "Google Calendar", // arb settingsIntegrationsGoogleCalendar
    icon: "calendar",
    tier: 3,
    dark: true,
    height: 440,
    heightSm: 500,
  },

  // Settings → Integrations → Google Calendar (app_en.arb settingsIntegrationsCalendar*): the
  // tagline, the sync switch and its destination, the show switch, and the busy-time calendars.
  // Calendar names are sample data. Every plan; busy time only matters to booking pages
  // (Specialist and up), which the section copy says.
  "integrations/google-calendar/how-it-works": {
    title: "Google Calendar",
    icon: "calendar",
    height: 404,
    heightSm: 470,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "calendar",
        title: "Google Calendar", // settingsIntegrationsGoogleCalendar
        sub: "Two-way sync with the Google calendars you choose.", // settingsIntegrationsGoogleCalendarTwoWayTagline
      },
      {
        type: "rows",
        at: 0.1,
        step: 0.07,
        items: [
          { title: "Sync my assigned events to Google Calendar", sub: "Add assigned events to · Work", toggle: { at: 0.2 } }, // settingsIntegrationsCalendarSyncToggle, …Destination
          { title: "Show my Google events in Workspace369", sub: "They appear read-only in your calendar. Only you can see them.", toggle: { at: 0.32 }, wrap: true }, // settingsIntegrationsCalendarShowToggle, …Helper
          { title: "Check busy times on", sub: "Work (Primary)", subTo: "Work (Primary), Family", swap: 0.46, focus: true }, // settingsIntegrationsCalendarConflicts, …Primary
        ],
      },
      { type: "payoff", at: 0.56, icon: "checkCircle", tone: "blue", text: "Calendar settings saved." }, // settingsIntegrationsCalendarSaved
    ],
  },

  // Booking page editor → Booking rules (lib/view/schedules/booking_pages/editor/sections/
  // rules_section.dart; app_en.arb settingsBookingPagesBookingRules, bookingEditorRulesSubtitle,
  // instantBooking, bookingEditorInstantHint, bookingEditorApproveTitle, bookingEditorApproveHoldHint,
  // bookingEditorVerifyEmail(Subtitle), bookingEditorMinimumNotice, bookingEditorBufferAfter,
  // bookingEditorBookUpTo, bookingEditorHours, bookingEditorDaysAhead, bookingEditorAllChangesSaved).
  // The page switches from Instant booking to Approve each request and turns on email
  // verification. Values are sample settings. Only shipped approval options are shown (FACTS §7).
  "features/online-booking": {
    title: "Booking rules", // settingsBookingPagesBookingRules
    icon: "calendar",
    tier: 3,
    height: 404,
    heightSm: 476,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "calendar",
        title: "Booking rules", // settingsBookingPagesBookingRules
        sub: "How bookings are confirmed and when clients can book.", // bookingEditorRulesSubtitle
      },
      {
        type: "rows",
        at: 0.1,
        step: 0.06,
        items: [
          { title: "Instant booking", sub: "Clients get a confirmed time straight away.", subTo: "Approve each request · The time is held for 48 hours.", swap: 0.3, focus: true, wrap: true }, // instantBooking, bookingEditorInstantHint, bookingEditorApproveTitle, bookingEditorApproveHoldHint
          { title: "Verify client email", sub: "Clients confirm a code before booking", toggle: { at: 0.42 } }, // bookingEditorVerifyEmail, …Subtitle
          { title: "Minimum notice", value: "4 h" }, // bookingEditorMinimumNotice, EventTimeLabel.duration
          { title: "Buffer after each booking", value: "15 min" }, // bookingEditorBufferAfter
          { title: "Book up to", value: "60 days ahead" }, // bookingEditorBookUpTo, bookingEditorDaysAhead
        ],
      },
      { type: "payoff", at: 0.56, icon: "checkCircle", tone: "blue", text: "All changes saved." }, // bookingEditorAllChangesSaved
    ],
  },

  // Approvals screen (lib/view/mcp/approvals/mcp_approval_screen.dart).
  "integrations/mcp": {
    scene: "mcp-approval",
    title: "Approvals",
    icon: "approvals",
    tier: 1,
    dark: true,
    height: 372,
    heightSm: 428,
  },

  // Invoice editor: title block pill (invoice_title_block.dart: draft, pending, viewed, paid),
  // items and totals, and the header's send buttons (invoice_header_widget.dart). Sending moves
  // Draft to Pending; the client opening it moves it to Viewed. There is no "Sent" status.
  // "Paid on" is the paid card's fact (`paidOn`).
  "features/invoices": {
    title: "Invoice Details",
    icon: "receipt",
    height: 380,
    heightSm: 408,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "receipt",
        title: "Invoice 26-12",
        sub: "Northwind Studio",
        pill: { text: "Draft", tone: "gray" },
        then: [
          { text: "Pending", tone: "blue", at: 0.33 },
          { text: "Viewed", tone: "blue", at: 0.44 },
          { text: "Paid", tone: "green", at: 0.58 },
        ],
      },
      {
        type: "rows",
        at: 0.1,
        items: [
          { title: "Logo design", value: "$1,200.00" },
          { title: "Brand guidelines", value: "$850.00" },
          { title: "Social templates", value: "$400.00" },
        ],
      },
      { type: "total", at: 0.22, label: "Total", value: "$2,450.00" },
      {
        type: "action",
        at: 0.24,
        buttons: [
          { label: "Send email", icon: "email", primary: true }, // arb sendEmailAction
          { label: "Send SMS", icon: "sms" },
        ],
        press: { index: 0, at: 0.3 },
        swap: 0.54,
        after: [{ type: "payoff", at: 0.58, text: "Paid on Oct 22, 2026" }],
      },
    ],
  },

  // Invoice Payments tab (invoice_payments_list_widget.dart): the summary tiles and a payment row
  // (method, "+amount" in green, date, AppStatusPill "Completed"). The client paid online by card
  // through the workspace's own Stripe account; nothing here is pressed in the app, and the
  // payment row (ringed) is the payoff.
  "features/payments": {
    title: "Payments",
    icon: "payments",
    height: 340,
    heightSm: 360,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "receipt",
        title: "Invoice 26-12",
        sub: "Northwind Studio",
        pill: { text: "Viewed", tone: "blue" },
        then: [{ text: "Paid", tone: "green", at: 0.5 }],
      },
      {
        type: "fields",
        at: 0.12,
        items: [
          { label: "Invoice Total", value: "$2,450.00" },
          { label: "Total Paid", value: "$0.00", to: "$2,450.00", swap: 0.46 },
          { label: "Balance Due", value: "$2,450.00", to: "$0.00", swap: 0.46 },
        ],
      },
      {
        type: "rows",
        at: 0.32,
        items: [
          {
            title: "Credit/Debit Card", // no arb key: lib/model/invoice/invoice_payment_model.dart
            sub: "Oct 22, 2026",
            icon: "checkCircle",
            iconTone: "green",
            value: "+$2,450.00",
            valueTone: "green",
            pill: { text: "Completed", tone: "green" },
            focus: true,
          },
        ],
      },
    ],
  },

  // The client's portal page (369.dev ClientPortalView.vue): header, Action items rows with their
  // buttons, and the Invoices list badge (Pending, then Paid). Pay opens the invoice's own page;
  // the portal takes no card inline. No Files or Messages: the client portal has neither.
  "features/client-portal": {
    title: "Client portal",
    icon: "portal",
    height: 420,
    heightSm: 472,
    blocks: [
      {
        type: "head",
        at: 0.04,
        avatar: "DR",
        title: "Dana Reyes",
        // no arb key: workspace-public-website components/ClientPortalView.vue
        sub: "Shared by Northwind Studio · Signed in as dana@reyes.test",
      },
      {
        type: "rows",
        at: 0.12,
        label: "Action items", // no arb key: workspace-public-website components/ClientPortal/ActionItems.vue
        items: [
          // no arb key: workspace-public-website utils/portal/clientPortal.ts (contract and proposal action items)
          { title: "Service agreement", sub: "Awaiting your signature", button: "Review & sign" },
          { title: "Website redesign", sub: "Awaiting your review · $9,200.00", button: "Review" },
        ],
      },
      {
        type: "rows",
        at: 0.26,
        label: "Invoices", // no arb key: workspace-public-website components/ClientPortal/InvoicesList.vue
        items: [
          {
            title: "#26-12",
            // no arb key: "{amount} due" / "{amount} paid" in InvoicesList.vue; the badge is
            // invoiceBadge() in utils/portal/clientPortal.ts (the humanized status, then Paid)
            sub: "$2,450.00 due",
            subTo: "$2,450.00 paid",
            swap: 0.5,
            pill: { text: "Pending", tone: "blue" },
            then: [{ text: "Paid", tone: "green", at: 0.5 }],
            focus: true,
          },
        ],
      },
      {
        type: "rows",
        // no arb key: workspace-public-website components/ClientPortal/InvoicesList.vue
        items: [{ title: "Payment history (1)", icon: "receipt", button: "Receipt", at: 0.56 }],
      },
    ],
  },

  // Recurring invoice record (plan_record_screen.dart, recurring_plan_facts.dart, timeline in
  // plan_record_timeline.dart). Autopay starts only after the client pays the first invoice and
  // agrees, which is why Collection reads "After first payment" until then; the client contact
  // turns it on, so the timeline's planTimelineAutopayTurnedOnBy names them. Cards render as
  // "Visa •••• 4242" (the arb examples for the card placeholders).
  "features/retainers-and-recurring-billing": {
    title: "Recurring invoice",
    icon: "renewCycle",
    height: 424,
    heightSm: 468,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "renewCycle",
        title: "Monthly care plan",
        sub: "Acme Studio · Dana Reyes",
        pill: { text: "Scheduled", tone: "blue" },
        then: [{ text: "Active", tone: "green", at: 0.3 }],
      },
      {
        type: "fields",
        at: 0.1,
        items: [
          { label: "Repeats", value: "Monthly on the 1st" },
          { label: "Amount", value: "$1,200.00" },
          { label: "Next invoice", value: "Nov 1", to: "Dec 1", swap: 0.3 },
          { label: "Collection", value: "After first payment", to: "Visa •••• 4242", swap: 0.52 },
        ],
      },
      {
        type: "rows",
        items: [
          { title: "Invoice 26-58 sent", icon: "send", at: 0.3 }, // arb planTimelineInvoiceSent
          { title: "Invoice 26-58 paid · $1,200.00", icon: "checkCircle", iconTone: "green", at: 0.42 }, // arb planTimelinePaid
        ],
      },
      { type: "payoff", at: 0.54, icon: "bolt", text: "Autopay turned on by Dana Reyes" },
    ],
  },

  // Proposal editor (proposal_editor_screen.dart): the status badge (_StatusBadge: Draft, Sent,
  // Accepted, Declined; there is no Viewed status, views are a separate blue "1 view" pill), line
  // items with an optional add-on (inline_line_item_row.dart, purple), the totals block and
  // "Send proposal". The client accepts on their proposal page; the app then offers Convert to
  // Invoice. A proposal is "Accepted", never "Signed".
  "features/quoting": {
    title: "Proposal",
    icon: "description",
    height: 424,
    heightSm: 460,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "description",
        title: "Website redesign",
        sub: "Acme Studio",
        pill: { text: "Draft", tone: "gray" },
        then: [
          { text: "Sent", tone: "blue", at: 0.33 },
          { text: "Accepted", tone: "green", at: 0.58 },
        ],
      },
      {
        type: "rows",
        at: 0.1,
        items: [
          { title: "Brand identity", value: "$3,200.00" },
          { title: "Website design", value: "$6,000.00" },
          // arb optional; the app draws it as a small purple "OPTIONAL" chip (inline_line_item_row.dart)
          { title: "SEO setup", value: "$800.00", pill: { text: "Optional", tone: "purple" } },
        ],
      },
      { type: "total", at: 0.22, label: "Total", value: "$9,200.00" },
      // no arb key: lib/view/proposals/widgets/blocks/totals_block_widget.dart
      { type: "total", at: 0.24, label: "With all add-ons", value: "$10,000.00" },
      {
        type: "action",
        at: 0.24,
        buttons: [{ label: "Send proposal", icon: "send", primary: true }],
        press: { index: 0, at: 0.3 },
        swap: 0.54,
        after: [
          {
            type: "payoff",
            at: 0.58,
            text: "Accepted",
            caption: "You can now convert this proposal to an invoice.", // no arb key: lib/controllers/proposal_controller.dart
          },
        ],
      },
    ],
  },

  // Contract detail (redesign/detail): status pill (contractStatusWaitingOnOthers,
  // contractsStatusSealing, contractsStatusCompleted), contractSignersInOrder with each signer's
  // note (contractSignerWaitingSince; contractSignerCountersigner for the workspace member),
  // contractActionCountersign, then the contractSignedOn banner and contractSignedCount.
  "features/contracts": {
    title: "Contracts",
    icon: "description",
    height: 360,
    heightSm: 414,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "description",
        title: "Service agreement",
        sub: "Acme Studio",
        pill: { text: "Waiting on others", tone: "blue" },
        then: [
          { text: "Sealing", tone: "blue", at: 0.54 },
          { text: "Completed", tone: "green", at: 0.62 },
        ],
      },
      {
        type: "rows",
        at: 0.1,
        label: "Signers · in order",
        items: [
          { title: "Dana Reyes", avatar: "DR", sub: "Waiting since Oct 24", subTo: "Signed Oct 26", swap: 0.28 },
          { title: "Maya Ortiz", avatar: "MO", sub: "Countersigner", subTo: "Signed Oct 27", swap: 0.5 },
        ],
      },
      {
        type: "action",
        at: 0.32,
        buttons: [{ label: "Countersign now", icon: "edit", primary: true }],
        press: { index: 0, at: 0.44 },
        swap: 0.52,
        after: [{ type: "payoff", at: 0.6, text: "Signed Oct 27", caption: "2 of 2 signed" }],
      },
    ],
  },

  // Bill time dialog (lib/view/time_tracking/widgets/bill_time_dialog.dart), opened from the Time
  // Tracking ⋯ menu. Keys: ttBillTime, ttAddTo, ttLines, ttLinesDetailed, ttEntryCount,
  // ttAddToNumber, ttBilledToInvoice (the success message). The invoice picker reads
  // "number · status · total" (_invoiceLabel); entries are grouped under their project, each with
  // "MMM d · first name" and an amount. Billing is its own action: approving a timesheet bills
  // nothing, and timesheet approvals are off by default, so the scene starts at billing.
  "features/time-tracking": {
    title: "Bill time",
    icon: "schedule",
    height: 436,
    heightSm: 470,
    blocks: [
      { type: "head", at: 0.04, icon: "schedule", title: "Bill time", sub: "Northwind Studio" },
      {
        type: "fields",
        at: 0.1,
        items: [
          { label: "Add to", value: "26-14 · Draft · $600.00" },
          { label: "Lines", value: "Detailed" },
        ],
      },
      {
        type: "rows",
        at: 0.16,
        label: "Brand identity",
        items: [
          { title: "Logo concepts", sub: "Oct 19 · Maya", value: "$180.00" },
          { title: "Brand guidelines", sub: "Oct 20 · Maya", value: "$150.00" },
        ],
      },
      { type: "total", at: 0.28, label: "2 entries", value: "$330.00" },
      {
        type: "action",
        at: 0.3,
        buttons: [{ label: "Add to 26-14", icon: "receipt", primary: true }],
        press: { index: 0, at: 0.4 },
        swap: 0.54,
        after: [{ type: "payoff", at: 0.58, icon: "receipt", text: "Billed $330.00 to 26-14" }],
      },
    ],
  },

  // Expense editor after a scan (lib/view/expenses/expense_create_chooser.dart, then
  // manage_expense_details/manage_expense_details_screen.dart; the beats follow the app's own
  // expenses unlock stage). Keys: scanInvoiceReceipt, enterManually, addExpense, name, amount,
  // expenseType, linkedProject, prefilledByAi (the editor's banner, auto_awesome icon), and the
  // stage's samples unlockExpensesVendor1 and unlockExpensesCat1. Expenses start at Specialist.
  // There is no billable toggle on an expense, so the scene doesn't show one.
  "features/expenses": {
    title: "Expenses",
    icon: "receipt",
    tier: 3,
    height: 380,
    heightSm: 428,
    blocks: [
      {
        type: "action",
        at: 0.04,
        buttons: [
          { label: "Scan invoice/receipt", icon: "documentScanner", primary: true },
          { label: "Enter manually", icon: "edit" },
        ],
        press: { index: 0, at: 0.12 },
        swap: 0.22,
        after: [
          { type: "head", at: 0.24, icon: "receipt", title: "Add Expense" },
          {
            type: "rows",
            at: 0.3,
            step: 0.05,
            items: [
              { title: "Name", value: "Northwind Supply" },
              { title: "Amount", value: "$84.20" },
              { title: "Expense Type", value: "Office supplies" },
              { title: "Linked Project", value: "Website redesign" },
            ],
          },
          {
            type: "payoff",
            at: 0.54,
            icon: "autoAwesome",
            tone: "blue",
            text: "✨ Prefilled by AI — review before saving.",
          },
        ],
      },
    ],
  },

  // AR Aging report (lib/view/accounting/reports/ar_aging_screen.dart; bucket bar
  // widgets/aging_bucket_bar.dart). Keys: reportArAging, agingTotalOutstanding, agingBucketCurrent,
  // agingBucketLabel1to30, agingBucketLabel31to60, agingBucketLabel90plus, agingActionViewInvoice,
  // agingActionSendReminder, agingActionRecordPayment. Bucket colours are Material colours in the
  // app (_bucketColor), drawn here in the nearest tones; an empty bucket (61-90) has no segment.
  // Sending asks "Send payment reminder?" first (skipped here). AR Aging starts at Specialist.
  "features/analytics": {
    title: "AR Aging",
    icon: "receipt",
    tier: 3,
    height: 352,
    heightSm: 386,
    blocks: [
      { type: "fields", at: 0.04, items: [{ label: "Total Outstanding", value: "$14,250.00" }] },
      {
        type: "bars",
        at: 0.1,
        segments: [
          { label: "Current", value: 6400, amount: "$6,400.00", tone: "green" },
          { label: "1-30", value: 4100, amount: "$4,100.00", tone: "amber" },
          { label: "31-60", value: 2350, amount: "$2,350.00", tone: "orange" },
          { label: "90+", value: 1400, amount: "$1,400.00", tone: "red" },
        ],
      },
      {
        type: "rows",
        at: 0.26,
        label: "Northwind Studio",
        items: [
          // "{n} days": no arb key: lib/view/accounting/reports/ar_aging_screen.dart (_invoiceRow)
          { title: "26-12", sub: "47 days", value: "$2,350.00", focus: true },
        ],
      },
      {
        type: "action",
        at: 0.34,
        buttons: [
          { label: "View invoice", icon: "description" },
          { label: "Send reminder", icon: "send", primary: true },
        ],
        press: { index: 1, at: 0.44 },
        swap: 0.54,
        after: [
          // no arb key: lib/controllers/invoice_reminders_controller.dart (the success message)
          { type: "payoff", at: 0.58, text: "Payment reminder sent" },
        ],
      },
    ],
  },

  // Calendar event details panel (lib/view/schedules/event_preview/event_details_panel.dart).
  // Status chip tones from calendarEventStatusTone (utils/event_display_format.dart): Tentative
  // amber, Confirmed blue, Completed green (arb tentative, confirmed, completed). The Status row
  // (statusLabel) opens showEventStatusMenu on tap, so the pointer taps the chip and it becomes
  // Completed. Rows: calPreviewWhen, calPreviewClient, teamLabel (first names). Footer:
  // calPreviewMessageClient, calPreviewInvoiceThis (it opens a new invoice for the client; it
  // doesn't bill the event, so it isn't pressed). Payoff: eventMarkedAs. Team calendar is on every
  // plan; booking pages (Specialist) aren't shown.
  "features/schedule": {
    title: "Event Details",
    icon: "schedule",
    height: 384,
    heightSm: 418,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "schedule",
        title: "Website kickoff",
      },
      {
        type: "fields",
        at: 0.1,
        items: [
          { label: "When", value: "Tue, Oct 27 · 10:00 AM – 11:00 AM" },
          { label: "Client", value: "Acme Studio" },
          { label: "Team", value: "Maya" },
          {
            label: "Status",
            pill: { text: "Confirmed", tone: "blue" },
            then: [{ text: "Completed", tone: "green", at: 0.46 }],
            press: 0.42,
          },
        ],
      },
      {
        type: "action",
        at: 0.2,
        buttons: [
          { label: "Message client", icon: "chatBubbleOutline" },
          { label: "Invoice this", icon: "receipt" },
        ],
      },
      { type: "payoff", at: 0.56, text: "Event marked as Completed" },
    ],
  },

  // Workflow editor, Flow tab (lib/view/workflows/workflow_page.dart, widgets/workflow_page_header.dart,
  // flow/flow_node_card.dart; step names from helpers/workflow_catalog.dart). Keys:
  // workflowsStatusDraft (amber, workflowLifecycleTone), workflowsPageRunsThisWeek,
  // workflowsTriggerScheduleCreated + workflowsCategoryTrigger, workflowsFlowWaitUntilBefore +
  // workflowsWaitUntilFieldAppointmentStart, workflowsSummaryEmailClient + workflowsCategoryMessage,
  // workflowsSummaryNotifyAll + workflowsCategoryTask, workflowsPageTest, workflowsPageTestDone.
  // Node icon colours are WorkflowTokens (workflow_tokens.dart), drawn in the nearest tones. An
  // email step, so it holds on Cadet (email automations; Voyager adds text steps). A test run
  // doesn't count toward "runs this week", so that line stays.
  "features/automation": {
    title: "Workflows",
    icon: "accountTree",
    height: 410,
    heightSm: 440,
    blocks: [
      {
        type: "head",
        at: 0.04,
        icon: "accountTree",
        title: "Appointment reminder",
        sub: "No runs this week",
        // Tested as a draft, then activated: the scene rests on Active.
        pill: { text: "Draft", tone: "amber" },
        then: [{ text: "Active", tone: "green", at: 0.8 }],
      },
      {
        type: "rows",
        at: 0.1,
        step: 0.06,
        items: [
          { title: "Appointment booked", sub: "Trigger", icon: "calendar", iconTone: "blue" },
          { title: "1 day before the appointment start", icon: "schedule" },
          { title: "Email the client", sub: "Message", icon: "email", iconTone: "red" },
          { title: "Notify all teammates", sub: "Task", icon: "bellAlert" },
        ],
      },
      {
        type: "action",
        at: 0.36,
        buttons: [{ label: "Test", icon: "playArrow", primary: true }],
        press: { index: 0, at: 0.42 },
        swap: 0.5,
        after: [
          { type: "payoff", at: 0.53, text: "Test run finished" },
          {
            type: "action",
            at: 0.6,
            buttons: [{ label: "Activate", icon: "check", primary: true }],
            press: { index: 0, at: 0.7 },
            out: 0.78,
          },
        ],
      },
    ],
  },

  // Inbox (lib/view/inbox/widgets/conversation_list/conversation_list_item.dart: the row and its
  // channel badges, _channelConfig: Email green, SMS blue, Call purple; thread/channel_divider.dart
  // and thread/call_message_card.dart in the thread). Arb: inbox. One person's email, text and
  // missed call land in one conversation, and its row lists every channel it has had. Calls and
  // texts start at Voyager.
  "features/communications": {
    title: "Inbox",
    icon: "email",
    tier: 2,
    height: 400,
    heightSm: 420,
    blocks: [
      {
        type: "rows",
        at: 0.04,
        step: 0.06,
        items: [
          {
            title: "Dana Reyes",
            avatar: "DR",
            sub: "Can we move Thursday's call to 2 PM?",
            // no arb key: the server's call preview (Workspace369-API inboxCallPreviewSync.ts)
            subTo: "Missed call",
            swap: 0.56,
            // no arb key: conversation_list_item.dart _channelConfig
            pills: [
              { text: "Email", tone: "green" },
              { text: "SMS", tone: "blue", at: 0.46 },
              { text: "Call", tone: "purple", at: 0.56 },
            ],
          },
          { title: "Priya Nair", avatar: "PN", sub: "Running 5 minutes late", pills: [{ text: "SMS", tone: "blue" }] },
        ],
      },
      {
        type: "rows",
        at: 0.3,
        label: "Dana Reyes",
        items: [
          // no arb key: the channel labels in thread/channel_divider.dart; the messages are samples
          { title: "Email", icon: "email", iconTone: "green", sub: "Can we move Thursday's call to 2 PM?", value: "9:41 AM", at: 0.34 },
          { title: "SMS", icon: "sms", iconTone: "blue", sub: "Sure, 2 PM works. See you then.", value: "9:52 AM", at: 0.44 },
          // no arb key: thread/call_message_card.dart ("Missed call", a red missed-call phone icon)
          { title: "Missed call", icon: "phoneMissed", iconTone: "red", value: "10:30 AM", at: 0.54, focus: true },
        ],
      },
    ],
  },

  // MCP page, "How it works": Settings → AI connections (lib/view/mcp/mcp_admin_screen.dart): the
  // Access switch (mcp_access_section.dart; arb aiConnectionsAccess, aiConnectionsAllow,
  // aiConnectionsAllowDescription), Connected apps (mcp_connection_tile.dart; aiConnectionsConnectedApps,
  // aiConnectionsNeverUsed, aiConnectionsUsedWhen, aiConnectionsJustNow, aiConnectionsPermissionCount,
  // gray) and the Activity log (mcp_audit_log_section.dart; aiConnectionsActivityLog,
  // aiConnectionsAllowed, green). The Activity log rows are a simplified rendering of the app's
  // Time / App / Tool / Result table: each row keeps the tool id in mono, "Claude · just now" and
  // the Result pill; the token count the app adds to charged calls is left off. MCP is off until an admin turns it on.
  "integrations/mcp/how-it-works": {
    title: "AI connections",
    icon: "bolt",
    height: 420,
    heightSm: 470,
    blocks: [
      {
        type: "rows",
        at: 0.04,
        label: "Access",
        items: [
          {
            title: "Allow AI connections",
            sub: "When on, members can connect AI apps to this workspace.",
            wrap: true,
            toggle: { at: 0.16 },
            focus: true,
          },
        ],
      },
      {
        type: "rows",
        at: 0.24,
        label: "Connected apps",
        items: [
          {
            title: "Claude",
            logo: "/images/ai-connections/ai_logo_claude.svg",
            sub: "Maya Ortiz · never used",
            wrap: true,
            subTo: "Maya Ortiz · used just now",
            swap: 0.44,
            pill: { text: "9 permissions", tone: "gray" },
          },
        ],
      },
      {
        type: "rows",
        at: 0.36,
        step: 0.08,
        label: "Activity log",
        items: [
          // no arb key: tool ids from the MCP server (Workspace369-API functions-mcp)
          { title: "clients_search", mono: true, sub: "Claude · just now", pill: { text: "Allowed", tone: "green" } },
          { title: "invoices_create_draft", mono: true, sub: "Claude · just now", pill: { text: "Allowed", tone: "green" }, focus: true },
        ],
      },
    ],
  },

  // MCP page, "What a connector can work with": Workspace369's consent page, served by the MCP
  // server (Workspace369-API functions-mcp/src/oauth/consentPage.ts). No arb keys: every string is
  // in that file (approve-heading, permissions-title, AREA_COPY names and descriptions, the level
  // segments None / View / Edit / Edit and send, approve-button and its "Approving…" state). The
  // areas sit in a dialog opened from the page; they're drawn on the page here. Approving sends the
  // browser back to the AI app, so there is no success frame.
  "integrations/mcp/connects": {
    title: "Connect Claude to Workspace369",
    icon: "portal",
    height: 414,
    heightSm: 480,
    blocks: [
      {
        type: "rows",
        at: 0.04,
        label: "Choose a workspace",
        items: [{ title: "Studio North", icon: "checkCircle", iconTone: "blue" }],
      },
      {
        type: "rows",
        at: 0.12,
        label: "Permissions for Claude",
        items: [
          {
            title: "Clients",
            sub: "Client records and lead sources",
            wrap: true,
            pill: { text: "None", tone: "gray" },
            then: [{ text: "Edit", tone: "blue", at: 0.24 }],
          },
          {
            title: "Invoices",
            sub: "Invoices, payments, and sending to clients",
            wrap: true,
            pill: { text: "None", tone: "gray" },
            then: [{ text: "Edit and send", tone: "blue", at: 0.3 }],
          },
          {
            title: "Proposals",
            sub: "Proposals, outcomes, and sending to clients",
            wrap: true,
            pill: { text: "None", tone: "gray" },
            then: [{ text: "View", tone: "blue", at: 0.36 }],
          },
        ],
      },
      {
        type: "action",
        at: 0.4,
        buttons: [{ label: "Approve access", primary: true }],
        press: { index: 0, at: 0.5 },
        swap: 0.58,
        after: [
          { type: "action", at: 0.58, buttons: [{ label: "Approving…", primary: true }] },
        ],
      },
    ],
  },

  // MCP page, "What it will not do on its own": the Approvals list and its bulk flow
  // (lib/view/mcp/approvals/mcp_approvals_screen.dart; pills and tones in mcp_approval_parts.dart:
  // Waiting amber, Running blue, Done green). The section's "Select all (2)" link
  // (mcpApprovalsSelectAll) ticks each row's checkbox and hides itself; the bulk bar
  // (AppBulkActionBar: selectedCount, mcpApprovalsBulkApprove, mcpApprovalDecline) then shows. It
  // sits above the list in the app and under it here. Approve opens the confirm dialog
  // (mcpApprovalsBulkApproveTitle, mcpApprovalsBulkApproveBody, cancel,
  // mcpApprovalsBulkApproveConfirm, laid over the list as the app's modal is); confirming runs each request (Running, then Done, captions
  // mcpApprovalApprovedByCaption + mcpApprovalYou) and the snackbar says mcpApprovalsBulkApproved.
  // The bar's "Approving 1 of 2…" progress is left out. Nothing moves until someone presses.
  "integrations/mcp/extra-0": {
    title: "Approvals",
    icon: "approvals",
    height: 312,
    heightSm: 392,
    blocks: [
      {
        type: "rows",
        at: 0.04,
        step: 0.05,
        label: "Waiting for you",
        labelAction: { text: "Select all (2)", press: 0.16, out: 0.21 },
        items: [
          {
            // no arb key: request headlines come from Workspace369-API functions-mcp/src/approvals/summaries.ts
            title: "Send invoice #1042 for Acme Co by email",
            wrap: true,
            check: 0.19,
            logo: "/images/ai-connections/ai_logo_claude.svg",
            sub: "Claude · 2 min ago",
            // No-break spaces keep a wrapped phone caption to "Approved by you ·" / "just now".
            subTo: "Approved by you\u00a0· just\u00a0now",
            swap: 0.8,
            pill: { text: "Waiting", tone: "amber" },
            then: [
              { text: "Running", tone: "blue", at: 0.74 },
              { text: "Done", tone: "green", at: 0.8 },
            ],
          },
          {
            title: "Send a contract for signature",
            wrap: true,
            check: 0.19,
            logo: "/images/ai-connections/ai_logo_claude.svg",
            sub: "Claude · 9 min ago",
            // No-break spaces keep a wrapped phone caption to "Approved by you ·" / "just now".
            subTo: "Approved by you\u00a0· just\u00a0now",
            swap: 0.8,
            pill: { text: "Waiting", tone: "amber" },
            then: [
              { text: "Running", tone: "blue", at: 0.76 },
              { text: "Done", tone: "green", at: 0.82 },
            ],
          },
        ],
      },
      {
        type: "action",
        at: 0.22,
        lead: "2 selected",
        buttons: [
          { label: "Approve", icon: "check", primary: true },
          { label: "Decline" },
        ],
        press: { index: 0, at: 0.34 },
        swap: 0.74,
        after: [
          {
            type: "action",
            at: 0.42,
            title: "Approve 2 requests?",
            body: "Each one runs now, so its emails, texts or documents go out to your clients. This can't be undone.",
            buttons: [{ label: "Cancel" }, { label: "Approve and send 2", primary: true }],
            press: { index: 1, at: 0.68 },
            out: 0.74,
          },
          { type: "payoff", at: 0.84, text: "2 requests approved" },
        ],
      },
    ],
  },

  // MCP page, "How this differs": AI usage per tool call (components/motion/McpUsageScene.vue
  // documents its strings and the read < write < send ratio).
  "integrations/mcp/extra-1": {
    scene: "mcp-usage",
    title: "AI connections",
    icon: "bolt",
    height: 420,
    heightSm: 470,
  },

  // MCP page, "Operational fit": a question in the AI app becomes an invoices_search call and its
  // overdue rows (components/motion/McpChatScene.vue). On this page the stage takes the place of
  // the template's generic fit steps.
  "integrations/mcp/fit": {
    scene: "mcp-chat",
    title: "Claude",
    icon: "chatBubbleOutline",
    height: 404,
    heightSm: 470,
  },

  // The MCP page's motion outside a stage. Each key switches on a component in
  // pages/integrations/[slug].vue; the component's own comment names its sources.
  // Under "Works inside": the apps that connect over MCP and the automation tools that use
  // webhooks and the Builder API (components/motion/McpAppLanes.vue).
  "integrations/mcp/apps": { scene: "app-lanes" },
  // Setup: the steps as a timeline that fills as the reader scrolls (McpSetupTimeline.vue).
  "integrations/mcp/setup": { scene: "setup-timeline" },
  // Use cases: each card plays the result it leaves (McpCaseOutcome.vue), in the order of the
  // page's useCases. Tool ids from the MCP server (Workspace369-API functions-mcp); "Draft" is the
  // invoice status (invoice_title_block.dart), "Waiting" mcpApprovalPillWaiting.
  "integrations/mcp/use-cases": {
    cases: [
      {
        tools: ["time_entries_list", "invoices_create_draft"],
        title: "Invoice 26-14",
        sub: "Acme Co · $1,850.00",
        pill: { text: "Draft", tone: "gray" },
      },
      {
        tools: ["projects_create", "tasks_create"],
        title: "Website refresh",
        sub: "Northwind Studio · 2 tasks",
      },
      {
        // no arb key: the request headline is inboxSendMessage's in Workspace369-API
        // functions-mcp/src/approvals/summaries.ts
        tools: ["search_workspace_messages", "inbox_send_message"],
        title: "Send an email to dana@reyes.test",
        sub: "Claude · just now",
        pill: { text: "Waiting", tone: "amber" },
      },
    ],
  },
  // FAQ: the Send without asking switch under this question (McpSendToggle.vue).
  "integrations/mcp/faq": { question: "Can it send things without asking?" },
};
